import React from 'react';
import { FiCheckCircle, FiClock, FiShield, FiZap, FiBarChart2, FiMail,  FiGlobe,  FiAlertTriangle } from 'react-icons/fi';

const AdmissionDropOff = () => {
  const dropOffReasons = [
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'Lengthy and Complicated Application Forms',
      description: 'When students encounter lengthy, confusing, or repetitive application forms, they often abandon the process. A form that takes 30 minutes or more to complete is a major barrier. Students and parents simply don\'t have the time or patience.',
      stat: '60% of applicants abandon forms longer than 15 minutes',
      solution: 'Truyon streamlines applications with smart forms that auto-fill and only ask relevant questions.',
      color: 'bg-indigo-50'
    },
    {
      icon: <FiAlertTriangle className="w-6 h-6" />,
      title: 'Document Submission Confusion',
      description: 'Students often don\'t know what documents are required, in what format, or what size. They upload incorrect files, get frustrated, and give up. The lack of clear guidance creates anxiety and drop-offs.',
      stat: '40% of drop-offs happen during document submission',
      solution: 'Truyon provides clear document requirements, automatic validation, and format guidance.',
      color: 'bg-amber-50'
    },
    {
      icon: <FiShield className="w-6 h-6" />,
      title: 'Uncertainty About Application Status',
      description: 'After submitting an application, students enter a period of anxious waiting. They don\'t know if their application was received, if documents are verified, or what happens next. This uncertainty often leads them to apply to other institutions.',
      stat: '50% of applicants apply to multiple institutions due to status uncertainty',
      solution: 'Truyon provides real-time application tracking and automated status updates.',
      color: 'bg-rose-50'
    },
    {
      icon: <FiMail className="w-6 h-6" />,
      title: 'Poor Communication and Follow-Up',
      description: 'Many institutions fail to communicate with applicants after submission. No confirmation emails, no updates, no reminders. Students feel forgotten and assume their application is lost. They move on to institutions that keep them informed.',
      stat: '70% of applicants prefer institutions with regular communication',
      solution: 'Truyon automates communication at every stage—confirmation, verification, interview, and offer.',
      color: 'bg-purple-50'
    },
    {
      icon: <FiBarChart2 className="w-6 h-6" />,
      title: 'No Mobile-Friendly Application Process',
      description: 'Most students and parents complete applications on their phones. If your application process isn\'t mobile-friendly, you\'re losing applicants. Forms that don\'t work on phones are a major barrier to completion.',
      stat: '80% of students use mobile devices for college applications',
      solution: 'Truyon is mobile-first, ensuring smooth applications on any device.',
      color: 'bg-blue-50'
    },
    {
      icon: <FiClock className="w-6 h-6" />,
      title: 'Delayed Admission Decisions',
      description: 'Students want answers quickly. When institutions take weeks or months to communicate decisions, applicants move on. In the competitive education market, speed matters.',
      stat: '45% of applicants accept offers from faster-moving institutions',
      solution: 'Truyon automates decision workflows, reducing admission timeline by 70%.',
      color: 'bg-emerald-50'
    }
  ];

  const strategies = [
    {
      title: 'Simplify Your Application Form',
      description: 'Ask only what you need. Use smart logic to show relevant questions. Auto-fill where possible. Make it a 5-minute process, not 30 minutes.',
      icon: <FiZap className="w-5 h-5" />
    },
    {
      title: 'Provide Clear Document Guidance',
      description: 'Tell applicants exactly what they need to upload, in what format, and what size. Provide templates and examples.',
      icon: <FiShield className="w-5 h-5" />
    },
    {
      title: 'Send Automated Status Updates',
      description: 'Keep applicants informed at every stage. Confirmation email immediately after submission. Document verification updates. Interview schedule. Decision notifications.',
      icon: <FiMail className="w-5 h-5" />
    },
    {
      title: 'Make It Mobile-Friendly',
      description: 'Design your application process for phones. Responsive forms, easy uploads, and simple navigation.',
      icon: <FiGlobe className="w-5 h-5" />
    },
    {
      title: 'Speed Up Your Decision Process',
      description: 'Automate evaluation workflows. Reduce manual steps. Make faster decisions. Speed is a competitive advantage.',
      icon: <FiClock className="w-5 h-5" />
    },
    {
      title: 'Track and Analyze Drop-Offs',
      description: 'Use analytics to identify where applicants are dropping off. Fix those specific bottlenecks. Continuously improve.',
      icon: <FiBarChart2 className="w-5 h-5" />
    }
  ];

  return (
    <>
      {/* Introduction */}
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        Every year, thousands of students start the admission process but never complete it. They begin filling out forms, upload a few documents, and then—nothing. They abandon the process and never submit their applications.
      </p>
      
      <p className="text-lg text-slate-600 leading-relaxed mb-6">
        This is called <strong className="text-slate-900">admission drop-off</strong>, and it's one of the biggest challenges educational institutions face. You're losing potential students not because they're not interested, but because your application process is too difficult, too confusing, or too slow.
      </p>

      <p className="text-lg text-slate-600 leading-relaxed mb-8">
        Every applicant who drops off is a missed opportunity. Here's how a digital application process can stop the drop-offs and help you fill your seats.
      </p>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-indigo-600">60%</div>
          <div className="text-sm text-slate-500">Abandon forms longer than 15 minutes</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-amber-600">40%</div>
          <div className="text-sm text-slate-500">Drop-off during document submission</div>
        </div>
        <div className="bg-white rounded-xl p-6 text-center border">
          <div className="text-3xl font-extrabold text-rose-600">70%</div>
          <div className="text-sm text-slate-500">Prefer institutions with regular communication</div>
        </div>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6">Why Applicants Drop Off</h2>

      {/* Drop-Off Reasons */}
      {dropOffReasons.map((item, i) => (
        <div key={i} className={`${item.color} rounded-2xl p-6 mb-6 border`}>
          <div className="flex items-start gap-4">
            <div className="bg-white rounded-xl p-3 shadow-sm flex-shrink-0 mt-1">
              {item.icon}
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-slate-900 mb-2">{item.title}</h3>
              <p className="text-slate-600 leading-relaxed text-sm mb-4">{item.description}</p>
              <div className="bg-white/60 rounded-xl p-3 mb-3 border border-amber-200">
                <p className="text-sm font-semibold text-amber-600">{item.stat}</p>
              </div>
              <div className="bg-white rounded-xl p-4 border border-emerald-200">
                <div className="flex items-center gap-2 text-sm font-semibold text-emerald-600 mb-1">
                  <FiCheckCircle className="w-4 h-4" />
                  <span>Truyon's Solution</span>
                </div>
                <p className="text-sm text-slate-700">{item.solution}</p>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Strategies Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">How to Reduce Admission Drop-Offs</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {strategies.map((item, i) => (
            <div key={i} className="bg-indigo-50 rounded-xl p-4 border border-indigo-200">
              <div className="flex items-start gap-3">
                <div className="bg-white rounded-lg p-2 flex-shrink-0">
                  {item.icon}
                </div>
                <div>
                  <h4 className="font-bold text-slate-900 text-sm">{item.title}</h4>
                  <p className="text-xs text-slate-600 mt-1">{item.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Comparison Section */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8">
        <h3 className="text-xl font-bold text-slate-900 mb-4">Traditional vs. Digital Admission Process</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-red-50 rounded-xl p-4 border border-red-200">
            <div className="text-sm font-semibold text-red-700 mb-2">Traditional Process</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Lengthy paper forms</span></li>
              <li className="flex items-start gap-2">• <span>No clear document guidance</span></li>
              <li className="flex items-start gap-2">• <span>No application status visibility</span></li>
              <li className="flex items-start gap-2">• <span>Poor communication</span></li>
              <li className="flex items-start gap-2">• <span>Not mobile-friendly</span></li>
              <li className="flex items-start gap-2">• <span>Slow decision-making</span></li>
            </ul>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="text-sm font-semibold text-emerald-700 mb-2">Digital Process with Truyon</div>
            <ul className="text-sm text-slate-600 space-y-2">
              <li className="flex items-start gap-2">• <span>Simplified smart forms</span></li>
              <li className="flex items-start gap-2">• <span>Clear document requirements and validation</span></li>
              <li className="flex items-start gap-2">• <span>Real-time application tracking</span></li>
              <li className="flex items-start gap-2">• <span>Automated communication at every stage</span></li>
              <li className="flex items-start gap-2">• <span>Mobile-friendly application</span></li>
              <li className="flex items-start gap-2">• <span>Fast automated decision workflows</span></li>
            </ul>
          </div>
        </div>
      </div>

      {/* Why Truyon Section */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-3">Why Truyon Reduces Admission Drop-Offs</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📝</div>
            <h4 className="font-bold text-slate-900 text-sm">Smart Forms</h4>
            <p className="text-xs text-slate-600">5-minute application process with auto-fill</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📄</div>
            <h4 className="font-bold text-slate-900 text-sm">Clear Guidance</h4>
            <p className="text-xs text-slate-600">Document requirements and validation</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📱</div>
            <h4 className="font-bold text-slate-900 text-sm">Mobile-First</h4>
            <p className="text-xs text-slate-600">Complete applications from any device</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">💬</div>
            <h4 className="font-bold text-slate-900 text-sm">Real-Time Updates</h4>
            <p className="text-xs text-slate-600">Status tracking and automated communication</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">⚡</div>
            <h4 className="font-bold text-slate-900 text-sm">Fast Decisions</h4>
            <p className="text-xs text-slate-600">Automated workflows reduce decision time</p>
          </div>
          <div className="bg-white rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">📊</div>
            <h4 className="font-bold text-slate-900 text-sm">Analytics</h4>
            <p className="text-xs text-slate-600">Track drop-off points and optimize</p>
          </div>
        </div>
      </div>

      {/* The Applicant Journey */}
      <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6">
        <h3 className="text-lg font-bold text-slate-900 mb-3">The Improved Applicant Journey</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-indigo-50 rounded-xl p-4 border border-indigo-200 text-center">
            <div className="text-2xl mb-1">🚀</div>
            <h4 className="font-bold text-slate-900 text-sm">Start</h4>
            <p className="text-xs text-slate-600">Simple, mobile-friendly application</p>
          </div>
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200 text-center">
            <div className="text-2xl mb-1">📄</div>
            <h4 className="font-bold text-slate-900 text-sm">Submit</h4>
            <p className="text-xs text-slate-600">Clear guidance and instant validation</p>
          </div>
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200 text-center">
            <div className="text-2xl mb-1">🎉</div>
            <h4 className="font-bold text-slate-900 text-sm">Accept</h4>
            <p className="text-xs text-slate-600">Fast decisions and regular updates</p>
          </div>
        </div>
      </div>

      {/* The Business Case */}
      <div className="mt-8 bg-amber-50 rounded-2xl p-6 border border-amber-200">
        <h3 className="text-lg font-bold text-amber-900 mb-3">The Business Case for Reducing Drop-Offs</h3>
        <p className="text-amber-800 leading-relaxed mb-3">
          Every applicant who drops off represents lost revenue and missed potential. In a competitive education market, every student matters. Reducing drop-offs by even 10% can significantly increase your enrollment numbers.
        </p>
        <p className="text-amber-800 leading-relaxed mb-3">
          But the benefits go beyond numbers. A smooth application process creates positive word-of-mouth. Students and parents tell others about their experience. Your reputation grows. More applicants come. It's a virtuous cycle.
        </p>
        <p className="text-amber-800 leading-relaxed">
          <strong>Reducing drop-offs isn't just about filling seats—it's about building a better admissions experience for everyone.</strong>
        </p>
      </div>

      {/* Conclusion */}
      <div className="mt-8 bg-indigo-50 rounded-2xl p-6 border border-indigo-200">
        <h3 className="text-lg font-bold text-indigo-900 mb-2">Stop Losing Applicants with Truyon</h3>
        <p className="text-indigo-800 leading-relaxed">
          Every applicant who abandons your admission process is a missed opportunity. A student who could have contributed to your institution. A family who could have become part of your community.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          Truyon's digital admission process reduces drop-offs by simplifying applications, providing clear guidance, enabling real-time tracking, and automating communication. Students complete their applications. You fill your seats.
        </p>
        <p className="text-indigo-800 leading-relaxed mt-3">
          <strong>Truyon for admissions.</strong> Reduce drop-offs. Fill seats faster. Build a better admissions experience.
        </p>
      </div>
    </>
  );
};

export default AdmissionDropOff;